import Link from '@jetbrains/ring-ui/components/link/link'
import {skipToken} from '@reduxjs/toolkit/query'
import React, {memo, useCallback} from 'react'
import {graphql, useFragment} from 'react-relay'

import {useAppDispatch, useAppSelector} from '../../../../../../../../../../hooks/react-redux'
import {restApi} from '../../../../../../../../../../services/rest'
import Icon from '../../../../../../../../../common/Icon/Icon'
import RouterLink from '../../../../../../../../../common/RouterLink/RouterLink'
import {getFullBuildLogLinkParams} from '../../../../../../../../../packages/BuildLog/BuildLog.utils'
import TrackingActiveAreaProvider from '../../../../../../../contexts/TrackingActiveArea/TrackingActiveAreaContext'
import {
  useJobId,
  useLastRun,
  usePipelineExistingJob,
} from '../../../../../../../EditPipelinePage/EditPipelinePage.hooks'
import {
  pipelineDraft,
  pipelineError,
} from '../../../../../../../EditPipelinePage/slices/EditPipelinePage.slices'
import type {pipelineHeadFragment$key} from '../../../../../../../hooks/__generated__/pipelineHeadFragment.graphql'
import {usePipelineId} from '../../../../../../../hooks/pipeline'
import type {Step} from '../../../../../../../types'
import PipelineTextInfo from '../../../../../PipelineTextInfo/PipelineTextInfo'
import SettingsBlock from '../../../../../SettingsBlock/SettingsBlock'

import type {JobStepLastRunFragment$key} from './__generated__/JobStepLastRunFragment.graphql'
import {availableSoftwareLink} from './JobStep.consts'
import JobStepHotFields from './JobStepHotFields/JobStepHotFields'
import JobStepRunInDocker from './JobStepRunInDocker/JobStepRunInDocker'
import StepTypeSelect from './StepTypeSelect/StepTypeSelect'

import styles from './JobStep.css'

type Props = {
  step: Step
  originalIndex: number
  index: number
  onStepPropertiesChange: (
    index: number,
    properties: Array<{name: string; value: string | null}>,
  ) => void
  onStepPropertyChange: (index: number, name: string, value: string | null) => void
  onStepNameChange: (index: number, name: string) => void
  onStepTypeChange: (index: number, type: string) => void
  autoFocus?: boolean
  draggable?: boolean
  pipelineHeadKey: pipelineHeadFragment$key | null
}

const lastRunFragment = graphql`
  fragment JobStepLastRunFragment on Build {
    status
    rawId
  }
`
function JobStep({
  step,
  originalIndex,
  index,
  onStepPropertiesChange,
  onStepPropertyChange,
  onStepNameChange,
  onStepTypeChange,
  autoFocus,
  draggable,
  pipelineHeadKey,
}: Props) {
  const jobId = useJobId()!
  const existing = usePipelineExistingJob(jobId)
  const dispatch = useAppDispatch()
  const errorMessageSpotId = `cli_error_${jobId}_step_${index}`
  const onChangeName = useCallback(
    (name: string) => onStepNameChange(originalIndex, name),
    [originalIndex, onStepNameChange],
  )
  const onChangeType = useCallback(
    (type: string) => {
      dispatch(pipelineError.actions.removeError(errorMessageSpotId))

      onStepTypeChange(originalIndex, type)
    },
    [dispatch, errorMessageSpotId, onStepTypeChange, originalIndex],
  )
  const savedStep = existing?.steps?.[originalIndex]
  const id = usePipelineId()
  const isDeleted = useAppSelector(
    state => state.pipelines.pipelineDraft[id]?.deleted?.steps?.[jobId]?.includes(originalIndex),
  )
  const runKey = useLastRun(jobId, pipelineHeadKey)
  const run = useFragment<JobStepLastRunFragment$key>(lastRunFragment, runKey)
  const failureId = run?.status !== 'SUCCESS' ? run?.rawId : null
  const listStepsIndex = index + 1
  const {buildAnchor, pointType} = restApi.endpoints.getBuildTimeline.useQuery(
    failureId != null ? {buildId: failureId} : skipToken,
    {
      selectFromResult: ({data}) => {
        const {buildAnchor, type} =
          data?.stages.find(stage => stage.name.startsWith(`Step ${listStepsIndex}/`))?.point ?? {}
        return {buildAnchor, pointType: type}
      },
    },
  )
  const handlerDeleteStepButtonClick = () => {
    dispatch(pipelineDraft.actions.deleteStep({id, jobId, index: originalIndex}))

    dispatch(pipelineError.actions.removeError(errorMessageSpotId))
  }
  const settingsBlockId = `step-${jobId}-${originalIndex + 1}`
  const settingsBlockcCllapsedDefault = !!savedStep

  const commonProps = {
    step,
    autoFocus,
    onStepPropertiesChange,
    onStepPropertyChange,
    stepIndex: originalIndex,
  }

  return (
    <TrackingActiveAreaProvider>
      <SettingsBlock
        id={settingsBlockId}
        collapsible
        collapsedDefault={settingsBlockcCllapsedDefault}
        isDeleted={isDeleted}
        title={step.name}
        generatedTitle={`Step ${listStepsIndex}`}
        onChangeName={onChangeName}
        onDeleteClick={handlerDeleteStepButtonClick}
        onRestoreClick={() => {
          dispatch(pipelineDraft.actions.restoreStep({id, jobId, index: originalIndex}))
        }}
        updated={savedStep != null && step.name !== savedStep?.name}
        secondLine={
          buildAnchor != null && failureId != null ? (
            <div className={styles.failureLine}>
              <RouterLink
                params={getFullBuildLogLinkParams({
                  buildId: failureId,
                  line: buildAnchor,
                  expandState: [buildAnchor],
                  target: 'popup',
                  expandAll: false,
                })}
              >
                {WrapText => (
                  <>
                    <Icon name="finished_red" className={styles.icon} />
                    <WrapText className={styles.failure}>
                      {pointType === 'FAILURE' ? 'Failed' : 'Unknown event'}
                    </WrapText>
                  </>
                )}
              </RouterLink>
              <RouterLink
                className={styles.logLink}
                params={getFullBuildLogLinkParams({
                  buildId: failureId,
                  line: buildAnchor,
                  expandState: [buildAnchor],
                  target: 'popup',
                  expandAll: false,
                })}
              >
                {'Show build log'}
              </RouterLink>
            </div>
          ) : null
        }
        draggable={draggable}
      >
        <PipelineTextInfo>
          <Link target="_blank" href={availableSoftwareLink} rel="noreferrer">{`List`}</Link>
          {' of pre-installed software on the agent'}
        </PipelineTextInfo>
        <StepTypeSelect
          selectedType={step.type}
          onChange={onChangeType}
          updated={existing != null && step.type !== savedStep?.type}
        />
        {step.type && (
          <>
            <JobStepHotFields {...commonProps} showOnlyMain />
            <JobStepRunInDocker {...commonProps} />
            <JobStepHotFields {...commonProps} showOnlyAdvanced />
          </>
        )}
      </SettingsBlock>
    </TrackingActiveAreaProvider>
  )
}

export default memo<Props>(JobStep)
